import type { TimeOfDayDistribution, Track } from '@anytracker/core'

import styles from './Charts.module.css'
import { trackColour } from './colours.js'
import { CHART, PLOT_BOTTOM, PLOT_HEIGHT, countTicks } from './geometry.js'

const PLOT_LEFT = CHART.padLeft
const PLOT_WIDTH = CHART.width - CHART.padLeft - CHART.padRight

/**
 * When measurements happen, over a day (T-504).
 *
 * The distribution beside each track's time series in the report. It answers a
 * question the series cannot: a track logged every evening and a track logged
 * at random look identical on a line, and only one of them is a routine.
 *
 * Counts, always — including for a numeric track. The question here is "how
 * often, and when", not "how much", and averaging by hour would answer a
 * different question in the same rectangle.
 *
 * Buckets with nothing in them are drawn as gaps in the axis rather than as
 * bars of height zero. An hour nobody measured in and an hour measured as zero
 * are different claims, and this chart is about the first kind.
 */
export function DistributionChart({
  track,
  distribution,
}: {
  readonly track: Track
  readonly distribution: TimeOfDayDistribution
}) {
  const buckets = distribution.buckets
  if (buckets.length === 0) {
    return null
  }

  const max = Math.max(...buckets.map((bucket) => bucket.count))
  const ticks = countTicks(max)
  const top = ticks[ticks.length - 1] ?? max
  const colour = trackColour(track)

  // A slot per hour of the day, not per bucket that happens to exist — so
  // 08:00 sits in the same place on every track's page and a morning routine is
  // visible at a glance across the whole report.
  const slots = Math.max(1, Math.round((24 * 60) / distribution.bucketMinutes))
  const slotWidth = PLOT_WIDTH / slots
  const barWidth = Math.max(1, slotWidth * 0.7)

  const heightOf = (count: number) => (top <= 0 ? 0 : (count / top) * PLOT_HEIGHT)

  return (
    <figure className={styles['distribution']}>
      <svg
        viewBox={`0 0 ${String(CHART.width)} ${String(CHART.height)}`}
        role="img"
        aria-label={`When ${track.name} was recorded, by time of day.`}
        className={styles['chart']}
      >
        {ticks.map((tick) => {
          const y = PLOT_BOTTOM - heightOf(tick)
          return (
            <g key={tick}>
              <line
                x1={PLOT_LEFT}
                x2={PLOT_LEFT + PLOT_WIDTH}
                y1={y}
                y2={y}
                className={styles['grid']}
              />
              <text
                x={PLOT_LEFT - 6}
                y={y + 4}
                className={styles['axisLabel']}
                textAnchor="end"
              >
                {tick}
              </text>
            </g>
          )
        })}

        {buckets.map((bucket) => {
          const slot = Math.floor(bucket.startMinute / distribution.bucketMinutes)
          const x = PLOT_LEFT + slot * slotWidth + (slotWidth - barWidth) / 2
          const height = heightOf(bucket.count)
          return (
            <rect
              key={bucket.startMinute}
              x={x}
              y={PLOT_BOTTOM - height}
              width={barWidth}
              height={Math.max(height, 1)}
              fill={colour}
            >
              <title>{`${bucket.label}: ${String(bucket.count)}`}</title>
            </rect>
          )
        })}

        {/* Four labels, not twenty-four: the axis is a day, and a printed page
            cannot carry an hourly label legibly at this width. */}
        {[0, 6, 12, 18].map((hour) => (
          <text
            key={hour}
            x={PLOT_LEFT + (hour / 24) * PLOT_WIDTH}
            y={CHART.height - 8}
            className={styles['axisLabel']}
            textAnchor="middle"
          >
            {String(hour).padStart(2, '0')}:00
          </text>
        ))}
      </svg>
      <figcaption className={styles['kindNote']}>
        Recordings by time of day, in the zone each was captured in.
      </figcaption>
    </figure>
  )
}
